import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFormConfigurations } from '@/hooks/useFormConfigurations';
import { useSubmissions } from '@/hooks/useSubmissions';
import ConfigurableForm, { resolveOtherAnswer } from '@/components/base/ConfigurableForm';
import type { FormAnswers } from '@/components/base/ConfigurableForm';

const LOGO_SRC = '/assets/zschimmer-schwarz-logo.png';
const HARASSMENT_NOTICE =
  'A Zschimmer & Schwarz não tolera assédio sexual no trabalho ou qualquer discriminação relacionada; nem qualquer forma de intimidação é tolerada, como boatos, comportamento ameaçador ou humilhante, insultos, tratamento difamatório ou indigno.';
const ANONYMOUS_NOTICE =
  'Agora você está em área anônima. Todas as suas informações estão ocultas. A plataforma só volta ao normal quando você sair do questionário ou finalizar ele.';

export default function ColaboradorDenuncia() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formAnswers, setFormAnswers] = useState<FormAnswers>({});
  const [showNoticeModal, setShowNoticeModal] = useState(true);
  const [showAnonymousModal, setShowAnonymousModal] = useState(false);
  const { getForm } = useFormConfigurations();
  const { submitDenunciation } = useSubmissions();

  const config = getForm('denunciation');
  const isAnonymous = formAnswers['q-den-1'] === 'Não';
  const visibleConfig = useMemo(() => {
    if (!config || !isAnonymous) return config;

    return {
      ...config,
      questions: config.questions.filter((question) => question.id !== 'q-den-2' && question.id !== 'q-den-11'),
    };
  }, [config, isAnonymous]);

  const initialValues: FormAnswers = {
    'q-den-2': user?.name || '',
    'q-den-11': user?.email || '',
  };

  const handleAnswersChange = (answers: FormAnswers) => {
    const wasAnonymous = formAnswers['q-den-1'] === 'Não';
    const nowAnonymous = answers['q-den-1'] === 'Não';

    if (!wasAnonymous && nowAnonymous) {
      setShowAnonymousModal(true);
    }

    setFormAnswers(answers);
  };

  const handleSubmit = async (answers: FormAnswers) => {
    setSubmitting(true);
    setError('');

    try {
      const identificar = answers['q-den-1'] === 'Sim';

      await submitDenunciation({
        identificar,
        nomeIdentificado: identificar ?(answers['q-den-2'] as string) : undefined,
        setor: resolveOtherAnswer(answers, 'q-den-3') || '',
        assedioExplicado: !!answers['q-den-4'],
        tipoAssedio: resolveOtherAnswer(answers, 'q-den-5') || '',
        autoresNome: (answers['q-den-6'] as string) || '',
        arquivo: (answers['q-den-7'] as File | null) || null,
        detalhes: (answers['q-den-8'] as string) || '',
        nivelAssedio: Number(answers['q-den-9'] || 0),
        ultimaOcorrencia: (answers['q-den-10'] as string) || '',
        acessoRetorno: identificar ?(answers['q-den-11'] as string) || '' : '',
        observacoes: (answers['q-den-12'] as string) || '',
      });

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao enviar denúncia. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visibleConfig) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-portal-gray-500">Formulário não configurado.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div>
        <div className="bg-white rounded-xl border border-portal-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-green-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-portal-gray-900 mb-2">
            Denúncia Enviada com Sucesso!
          </h2>
          <p className="text-sm text-portal-gray-500 mb-2">
            Agradecemos por compartilhar. De forma sigilosa estaremos averiguando os fatos e daremos um retorno em breve.
          </p>
          <p className="text-sm font-medium text-portal-purple mb-4">
            Sua denúncia foi registrada com sucesso.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSubmitted(false); setFormAnswers({}); setShowNoticeModal(true); }}
              className="px-4 py-2.5 bg-portal-purple text-white text-sm font-medium rounded-lg hover:bg-portal-purple-dark transition-colors whitespace-nowrap"
            >
              Nova Denúncia
            </button>
            <button
              onClick={() => navigate('/colaborador')}
              className="px-4 py-2.5 border border-portal-gray-300 text-portal-gray-700 text-sm font-medium rounded-lg hover:bg-portal-gray-50 transition-colors whitespace-nowrap"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isAnonymous ?'anonymous-denunciation -m-4 md:-m-8 p-4 md:p-8 min-h-screen rounded-none md:rounded-xl' : ''}>
      {isAnonymous && (
        <style>
          {`
            .anonymous-denunciation {
              background: #0f172a;
              color: #e5e7eb;
            }
            .anonymous-denunciation .bg-white,
            .anonymous-denunciation form > div {
              background-color: #111827 !important;
              border-color: #334155 !important;
            }
            .anonymous-denunciation .text-portal-gray-900,
            .anonymous-denunciation .text-portal-gray-800,
            .anonymous-denunciation .text-portal-gray-700 {
              color: #f8fafc !important;
            }
            .anonymous-denunciation .text-portal-gray-600,
            .anonymous-denunciation .text-portal-gray-500,
            .anonymous-denunciation .text-portal-gray-400 {
              color: #cbd5e1 !important;
            }
            .anonymous-denunciation input,
            .anonymous-denunciation textarea {
              color: #f8fafc !important;
              border-color: #475569 !important;
            }
          `}
        </style>
      )}

      {isAnonymous && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800">
            <i className="ri-spy-line text-xl text-slate-100"></i>
          </div>
          <div>
            <p className="text-sm font-semibold">Navegação anônima ativa</p>
            <p className="text-xs text-slate-300">Dados pessoais ocultos neste questionário.</p>
          </div>
        </div>
      )}
      <h1 className="text-xl font-bold text-portal-gray-900 mb-2">Canal de Denúncias</h1>
      <p className="text-sm text-portal-gray-500 mb-6">
        Faça denúncias de forma anônima ou identificada. Sua segurança e bem-estar são prioridades.
      </p>

      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden mb-4">
        <div className="px-6 py-4 border-b border-portal-gray-100">
          <p className="text-xs text-portal-gray-400 uppercase tracking-wider font-medium">Bem Vindo Colaborador</p>
          <h2 className="text-sm font-bold text-portal-gray-900 mt-1">DENÚNCIA DE ASSÉDIO</h2>
        </div>
        <div className="p-6">
          <p className="text-xs text-portal-gray-500 mb-2">Código de Conduta 763 (Página 64)</p>
          <div className="bg-portal-purple rounded-lg p-4 mb-4">
            <p className="text-xs text-white/90 leading-relaxed">
              A empresa não tolera assédio sexual no trabalho ou qualquer discriminação relacionada, nem qualquer forma de intimidação e violência, como boatos, comportamentos ameaçadores ou humilhantes, insultos, tratamento difamatório ou indigno.
            </p>
          </div>
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20workplace%20respect%20and%20dignity%20concept%20silhouettes%20of%20people%20in%20harmony%20clean%20minimalist%20illustration%20dark%20blue%20and%20gray%20tones%20corporate%20style&width=600&height=180&seq=denunciation-banner-supabase&orientation=landscape"
            alt="Código de conduta"
            className="w-full h-28 object-cover rounded-lg"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
          <span>{error}</span>
        </div>
      )}

      <ConfigurableForm
        config={visibleConfig}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onAnswersChange={handleAnswersChange}
        submitLabel={submitting ?'Enviando...' : 'Enviar'}
        cancelLabel="Limpar formulário"
        onCancel={() => navigate('/colaborador')}
        disabled={submitting}
      />

      {showNoticeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNoticeModal(false)}></div>
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <img src={LOGO_SRC} alt="Zschimmer & Schwarz Brasil" className="mx-auto mb-4 h-20 w-auto object-contain" />
            <h2 className="mb-3 text-center text-lg font-bold text-portal-gray-900">Aviso importante</h2>
            <p className="text-sm leading-relaxed text-portal-gray-700">{HARASSMENT_NOTICE}</p>
            <button
              type="button"
              onClick={() => setShowNoticeModal(false)}
              className="mt-5 w-full rounded-lg bg-portal-purple px-4 py-2.5 text-sm font-semibold text-white hover:bg-portal-purple-dark"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {showAnonymousModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowAnonymousModal(false)}></div>
          <div className="relative w-full max-w-md rounded-2xl border border-slate-700 bg-slate-950 p-6 text-slate-100 shadow-xl">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-800">
              <i className="ri-spy-line text-3xl"></i>
            </div>
            <h2 className="mb-3 text-center text-lg font-bold">Área anônima</h2>
            <p className="text-sm leading-relaxed text-slate-300">{ANONYMOUS_NOTICE}</p>
            <button
              type="button"
              onClick={() => setShowAnonymousModal(false)}
              className="mt-5 w-full rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-white"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
